import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, CircularProgress, Pagination } from "@mui/material";
import { useDispatch } from "react-redux";
import { getIPHistory } from "../../../../actions/user";
import moment from "moment";

const BrowseHistoryView = ({ trafficData }) => {
    const dispatch = useDispatch();
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc"); // "desc" (newest) or "asc" (oldest)
    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        count: 0
    });

    const fetchHistory = useCallback(async (page = 1) => {
        if (!trafficData?.ip) return;
        setLoading(true);
        const res = await dispatch(getIPHistory(trafficData.ip, page, 50, sortOrder, startDate, endDate));
        if (res?.success) {
            setHistoryData(res.data.data || []);
            setPagination(res.data.pagination || { currentPage: 1, totalPages: 1, count: 0 });
        }
        setLoading(false);
    }, [dispatch, trafficData?.ip, sortOrder, startDate, endDate]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const page = parseInt(urlParams.get("historyPage")) || 1;
        if (page !== pagination.currentPage) {
            setPagination(prev => ({ ...prev, currentPage: page }));
        }
    }, [window.location.search]);

    useEffect(() => {
        fetchHistory(pagination.currentPage);
    }, [fetchHistory, pagination.currentPage]);

    const filteredHistory = historyData.filter(item =>
        item.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.path?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handlePageChange = (event, value) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("historyPage", value);
        window.history.replaceState(null, "", `${window.location.pathname}?${urlParams.toString()}`);
        setPagination({ ...pagination, currentPage: value });
    };


    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search browse history"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#EEEEEE",
                            "& fieldset": { borderColor: "transparent" },
                            "&:hover fieldset": { borderColor: "transparent" },
                            "&.Mui-focused fieldset": { borderColor: "transparent" },
                            height: "45px"
                        },
                        "& .MuiInputBase-input": {
                            textAlign: "center"
                        }
                    }}
                />
            </Box>

            <Box className="d-flex gap-2 mb-3">
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={startDate}
                    onChange={(e) => {
                        setStartDate(e.target.value);
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    label="Start Date"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#EEEEEE",
                            "& fieldset": { borderColor: "transparent" },
                            "&:hover fieldset": { borderColor: "transparent" },
                            "&.Mui-focused fieldset": { borderColor: "transparent" }
                        }
                    }}
                />
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={endDate}
                    onChange={(e) => {
                        setEndDate(e.target.value);
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    label="End Date"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#EEEEEE",
                            "& fieldset": { borderColor: "transparent" },
                            "&:hover fieldset": { borderColor: "transparent" },
                            "&.Mui-focused fieldset": { borderColor: "transparent" }
                        }
                    }}
                />
            </Box>

            <Box className="d-flex gap-2 mb-4">
                <button
                    onClick={() => {
                        setSortOrder("asc");
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    className="sofiaBold w-100 py-2"
                    style={{
                        border: sortOrder === "asc" ? "1px solid #1D1D1B" : "none",
                        borderRadius: "10px",
                        backgroundColor: sortOrder === "asc" ? "#FFF" : "#EEEEEE",
                        color: "#1D1D1B",
                        fontSize: "14px"
                    }}
                >
                    Oldest → Newest
                </button>
                <button
                    onClick={() => {
                        setSortOrder("desc");
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    className="sofiaBold w-100 py-2"
                    style={{
                        border: sortOrder === "desc" ? "1px solid #1D1D1B" : "none",
                        borderRadius: "10px",
                        backgroundColor: sortOrder === "desc" ? "#FFF" : "#EEEEEE",
                        color: "#1D1D1B",
                        fontSize: "14px"
                    }}
                >
                    Newest → Oldest
                </button>
            </Box>

            <Box>
                {loading ? (
                    <Box className="d-flex justify-content-center py-5">
                        <CircularProgress size={24} sx={{ color: "#1D1D1B" }} />
                    </Box>
                ) : filteredHistory.length > 0 ? (
                    filteredHistory.map((item, idx) => (
                        <Box key={idx} sx={{ py: 2, borderBottom: "1px solid #EEEEEE" }}>
                            <Typography className="sofiaBold" style={{ fontSize: "14px", color: "#1D1D1B" }}>
                                {moment(item.visitedAt).format("MM/DD/YYYY h:mmA")}
                            </Typography>
                            <Typography
                                className="sofiaRegular"
                                component="a"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    fontSize: "13px",
                                    color: "#007bff",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    wordBreak: "break-all",
                                    display: "block"
                                }}
                            >
                                {item.url}
                            </Typography>
                            {item.isCheckoutPage && (
                                <Typography
                                    className="sofiaBold"
                                    sx={{
                                        fontSize: "10px",
                                        display: "inline-block",
                                        backgroundColor: "#fee9eb",
                                        color: "#fb1c30",
                                        px: 1,
                                        borderRadius: "4px",
                                        mt: 0.5
                                    }}
                                >
                                    Checkout Page
                                </Typography>
                            )}
                        </Box>
                    ))
                ) : (
                    <Typography className="sofiaBold text-center py-5" sx={{ color: "#999" }}>
                        No browse history found
                    </Typography>
                )}
            </Box>

            {pagination.totalPages > 1 && (
                <Box className="d-flex justify-content-center align-items-center flex-column p-4 mt-2" style={{ borderTop: "1px solid #EEEEEE" }}>
                    <Pagination
                        count={pagination.totalPages}
                        page={pagination.currentPage}
                        onChange={handlePageChange}
                        size="small"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                fontFamily: "'Sofia Pro Regular', sans-serif",
                            },
                            "& .Mui-selected": {
                                backgroundColor: "#1D1D1B !important",
                                color: "#FFF !important",
                            }
                        }}
                    />
                    <Typography className="sofiaBold mt-2" style={{ color: "#9f9f9f", fontSize: "12px" }}>
                        {historyData.length} of {pagination.count} Page Views
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default BrowseHistoryView;
